/**
 * @description SignIn component for Lynx Admin portal (Self-Hosted UI)
 *
 * Key features:
 * - Email/password form for Wristband self-hosted login
 * - Auto-redirects to start OAuth flow if no authorization token exists
 * - Stores authorizationRequestToken in sessionStorage
 * - Sends portal=admin query param to backend login endpoint
 *
 * @dependencies
 * - apiClient: API calls to backend
 * - AuthContext: Check if already signed in
 *
 * @notes
 * - Self-hosted login flow (same as merchant frontend):
 *   1. User visits /sign-in → auto-redirects to GET /api/auth/login?portal=admin
 *   2. Backend redirects to Wristband /authorize on admin tenant domain
 *   3. Wristband redirects back to /sign-in?req=<JWT>
 *   4. Component stores token in sessionStorage, cleans URL
 *   5. User enters credentials → POST /api/auth/login
 *   6. Backend returns redirectUrl → frontend follows it
 *   7. Wristband creates auth session → callback → tokens
 * - No sign-up link (admin users are provisioned, not self-registered)
 * - No forgot password in V1 (admin accounts managed via Wristband dashboard)
 */

import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import apiClient from '../../services/api/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import {
  resolveOAuthUrlError,
  resolveSignInError,
  shouldRestartSignInFlow,
} from '../../utils/authErrorMessages';
import { ADMIN_DEFAULT_PATH } from '../../utils/adminNavActive';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';
const AUTH_REQUEST_TOKEN_KEY = 'wristband_auth_request_token';

function SignIn() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authorizationRequestToken, setAuthorizationRequestToken] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // If already signed in, Channel Partners until a dashboard exists
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      sessionStorage.removeItem(AUTH_REQUEST_TOKEN_KEY);
      navigate(ADMIN_DEFAULT_PATH, { replace: true });
    }
  }, [isLoaded, isSignedIn, navigate]);

  // Handle OAuth flow: check for ?req= param or redirect to start flow
  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn) return;

    const params = new URLSearchParams(location.search);
    const reqFromUrl = params.get('req');
    const urlError = params.get('error');

    if (urlError) {
      setError(resolveOAuthUrlError(urlError));
      window.history.replaceState({}, '', location.pathname);
      setIsCheckingAuth(false);
      return;
    }

    if (reqFromUrl) {
      sessionStorage.setItem(AUTH_REQUEST_TOKEN_KEY, reqFromUrl);
      setAuthorizationRequestToken(reqFromUrl);
      window.history.replaceState({}, '', location.pathname);
      setIsCheckingAuth(false);
      return;
    }

    const storedToken = sessionStorage.getItem(AUTH_REQUEST_TOKEN_KEY);
    if (storedToken) {
      setAuthorizationRequestToken(storedToken);
      setIsCheckingAuth(false);
      return;
    }

    // No authorization token — start OAuth flow with portal=admin
    const loginUrl = `${BACKEND_URL}/api/auth/login?portal=admin`;
    window.location.href = loginUrl;
  }, [location.search, location.pathname, isLoaded, isSignedIn]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    if (!authorizationRequestToken) {
      const loginUrl = new URL(`${BACKEND_URL}/api/auth/login`);
      loginUrl.searchParams.set('portal', 'admin');
      loginUrl.searchParams.set('email', email.trim().toLowerCase());
      window.location.href = loginUrl.toString();
      return;
    }

    try {
      const response = await apiClient.post('/auth/login', {
        email: email.trim().toLowerCase(),
        password,
        authorizationRequestToken,
      });

      if (response.data.success && response.data.redirectUrl) {
        sessionStorage.removeItem(AUTH_REQUEST_TOKEN_KEY);
        window.location.href = response.data.redirectUrl;
      } else {
        setError('Unexpected response from server. Please try again.');
        setIsSubmitting(false);
      }
    } catch (err) {
      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message;

      if (shouldRestartSignInFlow(errorCode)) {
        sessionStorage.removeItem(AUTH_REQUEST_TOKEN_KEY);
        window.location.href = `${BACKEND_URL}/api/auth/login?portal=admin`;
        return;
      }

      setError(resolveSignInError({
        errorCode,
        errorMessage,
        httpStatus: err.response?.status,
      }));
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (!isLoaded || isCheckingAuth) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
          <p className="text-sm text-slate-400">Preparing sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-600 mb-4">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Lynx Admin</h1>
          <p className="text-slate-400 text-sm mt-1">Internal platform management</p>
        </div>

        {/* Login form */}
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-400 ml-0.5">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                className="w-full bg-slate-900/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 transition-all focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20 outline-none text-sm"
                placeholder="admin@lynx.build"
                disabled={isSubmitting}
                autoComplete="email"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-400 ml-0.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(null); }}
                  className="w-full bg-slate-900/50 border border-slate-600/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 transition-all focus:border-indigo-500/50 focus:ring-2 focus:ring-indigo-500/20 outline-none text-sm"
                  placeholder="Enter your password"
                  disabled={isSubmitting}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-indigo-600 text-white font-medium rounded-xl px-4 py-3 text-sm transition-all hover:bg-indigo-500 active:scale-[0.98] disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-600 mt-6">
          Lynx Internal &middot; Authorized personnel only
        </p>
      </div>
    </div>
  );
}

export default SignIn;
